import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dispatch } from 'store/store';
import { HttpResponse, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import { getArgumentsEndpoint } from 'routes/BudgetModule/components/BudgetProposal/Resource/Analyse/helpers/getArgumentsEndpoint';

const renameArgument = async ({
    title,
    id,
}: {
    title: string;
    id: string;
}): Promise<{
    content: {
        message: string;
    };
}> => {
    const response = await RQAPICall({
        endpoint: `budget_proposals/arguments/${id}`,
        body: { title },
        method: 'PUT',
    });
    return response;
};
const deleteArgument = async (
    id: string,
): Promise<{
    content: {
        message: string;
    };
}> => {
    const response = await RQAPICall({
        endpoint: `budget_proposals/arguments/${id}`,
        method: 'DELETE',
    });
    return response;
};
const duplicateArgument = async ({
    title,
    id,
}: {
    title: string;
    id: string;
}): Promise<{
    content: {
        message: string;
    };
}> => {
    const response = await RQAPICall({
        endpoint: `budget_proposals/arguments/${id}/duplicate`,
        body: { title },
        method: 'POST',
    });
    return response;
};

export function useRenameArgumentMutation(
    resourceSoftwareId: string,
    trackingMetadata: pendo.Metadata,
): { mutate: ({ title, id }: { title: string; id: string }) => void } {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: renameArgument,

        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: data.content.message,
                snackType: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['arguments', resourceSoftwareId],
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.allArguments.rename,
                trackingMetadata,
            );
        },

        onError: (error: { message: string }) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
    return mutation;
}

export function useDuplicateArgumentMutation(
    resourceSoftwareId: string,
    trackingMetadata: pendo.Metadata,
): { mutate: ({ title, id }: { title: string; id: string }) => void } {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: duplicateArgument,

        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: data.content.message,
                snackType: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['arguments', resourceSoftwareId],
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.allArguments.duplicate,
                trackingMetadata,
            );
        },

        onError: (error: { message: string }) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
    return mutation;
}

export function useDeleteArgumentMutation(
    resourceSoftwareId: string,
    trackingMetadata: pendo.Metadata,
): { mutate: (id: string) => void } {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: deleteArgument,

        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: data.content.message,
                snackType: 'success',
            });
            queryClient.invalidateQueries({
                queryKey: ['arguments', resourceSoftwareId],
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.allArguments.delete,
                trackingMetadata,
            );
        },

        onError: (error: { message: string }) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
    return mutation;
}

export const useGetArguments = ({
    budgetProposalId,
    resourceSoftwareId,
}: {
    budgetProposalId: string;
    resourceSoftwareId: string;
}) => {
    const argumentEndpoint = getArgumentsEndpoint({
        resourceSoftwareId,
        budgetProposalId,
    });

    const { data, isLoading, isError } = useQuery({
        queryKey: ['arguments', resourceSoftwareId],

        queryFn: async () => {
            const response = await RQAPICallWithData<ArgumentT[]>({
                endpoint: argumentEndpoint,
                method: 'GET',
            });
            return response.content.data;
        },
    });

    return { data, isLoading, isError };
};

export const createArgument = async ({
    budgetProposalId,
    resourceSoftwareId,
    title,
}: {
    budgetProposalId: string;
    resourceSoftwareId: string;
    title: string;
}) => {
    const response = await RQAPICallWithData<HttpResponse<unknown>>({
        endpoint: getArgumentsEndpoint({
            budgetProposalId,
            resourceSoftwareId,
        }),
        body: { title },
        method: 'POST',
    });
    return response;
};
